import { Formik, FormikHelpers } from "formik";
import {
  ControlcenterMelcoModelsLocaleCultureString,
  ControlcenterMelcoModelsLocaleString,
  ControlcenterMelcoModelsLocaleStringListItem,
  useFormSubmit,
} from "melco-shared-logic";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { useQueryClient } from "react-query";
import { useEditLocaleStrings } from "../../../hooks/localestrings/useEditLocaleStrings";
import { DisplayEditLocaleStringsForm } from "./DisplayEditLocaleStringForm";
import { LocaleStringsListFiltersType } from "../../../hooks/localestrings/LocaleStringsListFilterType";
import { useGetLocaleStringById } from "../../../hooks/localestrings/useGetLocaleStringById";
import { useEffect, useState } from "react";

export type EditLocaleStringFormValueType = {
  text?: string | null;
  id: string;
  cultureCode?: string | null;
  strings: ControlcenterMelcoModelsLocaleCultureString[] | null;
};

type EditLocaleStringFormProps = {
  LocaleStringDetails: ControlcenterMelcoModelsLocaleStringListItem;
  onCloseDrawer: (closeDrawer: boolean) => void;
  localeStringsListFiltersValues: LocaleStringsListFiltersType | undefined;
  localeStringId: string;
};

export const EditLocaleStringForm: React.FC<EditLocaleStringFormProps> = ({
  LocaleStringDetails,
  localeStringId,
  onCloseDrawer,
  localeStringsListFiltersValues,
}) => {
  const queryClient = useQueryClient();

  // const queryKey = "localestrings";

  const editLocaleString = useEditLocaleStrings();

  const {
    query: { data: localeStringDetailsObjTemp },
    queryKey,
  } = useGetLocaleStringById(localeStringId, LocaleStringDetails.module ?? "");

  const [localeStringDetailsObj, setLocaleStringDetailsObj] =
    useState<ControlcenterMelcoModelsLocaleString>();

  useEffect(() => {
    setLocaleStringDetailsObj(localeStringDetailsObjTemp ?? undefined);
  }, [localeStringDetailsObjTemp]);

  const onSubmit = async (
    values: EditLocaleStringFormValueType,
    formikHelpers: FormikHelpers<EditLocaleStringFormValueType>
  ) => {
    await onGenerateSerialNumber(values, formikHelpers, {
      isManuallyResettingForm: true,
    });
  };

  const generateLicenseFormToInitialValues = (
    formvalues: ControlcenterMelcoModelsLocaleStringListItem
  ) => {
    const { id } = formvalues;

    const initialValues: EditLocaleStringFormValueType = {
      id: id ?? "",

      cultureCode: localeStringsListFiltersValues?.cultureCode ?? "",
      strings: localeStringDetailsObj?.strings ?? null,
    };

    return initialValues;
  };

  const initialValues: EditLocaleStringFormValueType = {
    id: LocaleStringDetails.id ?? "",
    cultureCode: localeStringsListFiltersValues?.cultureCode ?? null,
    strings: localeStringDetailsObj?.strings ?? null,
  };

  const handleLocaleString = async (
    values: EditLocaleStringFormValueType,
    formikHelpers: FormikHelpers<EditLocaleStringFormValueType>,
    options: {}
  ) => {
    let newEntity = await editLocaleString({
      id: values.id,
      cultureCode: values.cultureCode,
      strings: values.strings,
    });

    formikHelpers.resetForm({
      values: generateLicenseFormToInitialValues(
        newEntity ?? {
          id: null,
          strings: null,
        }
      ),
    });
    queryClient.setQueryData(queryKey, newEntity);
    onCloseDrawer(false);
  };

  const formSubmitToLocaleString = () => {
    return [
      async (
        values: EditLocaleStringFormValueType,
        formikHelpers: FormikHelpers<EditLocaleStringFormValueType>
      ) => {
        return handleLocaleString(values, formikHelpers, {});
      },
      {
        translationOptions: {
          batchNumber: 1,
        },
        translationPrefix: ["localestrings.actions", "edit"].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(queryKey);
        },
      },
    ] as const;
  };

  const onGenerateSerialNumber = useFormSubmit(...formSubmitToLocaleString());

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        <DirtyFormPrompt>
          <DisplayEditLocaleStringsForm />
        </DirtyFormPrompt>
      </Formik>
    </>
  );
};
